import { useCallback, useEffect, useState } from 'react';
import {
  fetchAccess,
  invite,
  setUserAccess,
  withdrawInvite,
  type Access,
  type AdminUser,
  type Invite,
} from '../admin.js';
import type { Me } from '../identity.js';
import { isAbortError } from '../project.js';
import { AdminPrompts } from './AdminPrompts.js';

/**
 * The admin pages, about the platform rather than a project, so outside the IDE:
 * Access at /admin (prd-deltas §22) — people waiting to be let in, everyone with an
 * account, invitations — and Prompts at /admin/prompts (§31) — every prompt Civil
 * sends, editable.
 */

export const ADMIN_PAGES = [
  { path: '/admin', title: 'Access' },
  { path: '/admin/prompts', title: 'Prompts' },
] as const;

export function Admin({ me }: { me: Me }) {
  return window.location.pathname.replace(/\/+$/, '') === '/admin/prompts' ? <AdminPrompts me={me} /> : <AccessPage me={me} />;
}

/** The top of every admin page: where you are, who you are, the way back. */
export function AdminHeader({ me, current }: { me: Me; current: (typeof ADMIN_PAGES)[number]['path'] }) {
  return (
    <header className="admin-top">
      <span className="brand">Civil</span>
      <nav className="admin-tabs" aria-label="Admin pages">
        {ADMIN_PAGES.map((p) => (
          <a key={p.path} href={p.path} className={p.path === current ? 'admin-tab current' : 'admin-tab'} aria-current={p.path === current ? 'page' : undefined}>
            {p.title}
          </a>
        ))}
      </nav>
      <span className="admin-me">{me.email}</span>
      <a className="link" href="/">Back to Civil</a>
    </header>
  );
}

type Load =
  | { status: 'loading' }
  | { status: 'ready'; users: AdminUser[]; invites: Invite[] }
  | { status: 'error'; message: string };

function AccessPage({ me }: { me: Me }) {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  // What the last change did, in the server's words, or why it was refused.
  const [effect, setEffect] = useState<{ text: string; failed: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback((signal?: AbortSignal) => {
    void fetchAccess(signal)
      .then(({ users, invites }) => setLoad({ status: 'ready', users, invites }))
      .catch((error: unknown) => {
        if (!isAbortError(error)) setLoad({ status: 'error', message: (error as Error).message });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const act = (work: () => Promise<string>) => {
    setBusy(true);
    void work()
      .then((text) => setEffect({ text, failed: false }))
      .catch((error: unknown) => setEffect({ text: (error as Error).message, failed: true }))
      .finally(() => {
        setBusy(false);
        refresh();
      });
  };

  return (
    <div className="admin">
      <AdminHeader me={me} current="/admin" />

      {effect ? <p className={effect.failed ? 'admin-effect failed' : 'admin-effect'}>{effect.text}</p> : null}

      {load.status === 'loading' ? <p className="muted">Loading…</p> : null}
      {load.status === 'error' ? <p className="gate-error">{load.message}</p> : null}
      {load.status === 'ready' ? (
        <>
          <Users
            title="Requests"
            empty="Nobody has asked to be let in."
            users={load.users.filter((u) => u.access === 'pending' && u.requestedAt)}
            me={me}
            busy={busy}
            act={act}
          />
          <InviteForm busy={busy} act={act} />
          <Invites invites={load.invites} busy={busy} act={act} />
          <Users
            title="Everyone"
            empty="No accounts yet."
            // Signed in without asking is not a request, so it is listed here, quietly.
            users={load.users.filter((u) => !(u.access === 'pending' && u.requestedAt))}
            me={me}
            busy={busy}
            act={act}
          />
        </>
      ) : null}
    </div>
  );
}

type Act = (work: () => Promise<string>) => void;

function Users({ title, empty, users, me, busy, act }: {
  title: string;
  empty: string;
  users: AdminUser[];
  me: Me;
  busy: boolean;
  act: Act;
}) {
  return (
    <section className="admin-section">
      <h2>
        {title} <span className="admin-count">{users.length}</span>
      </h2>
      {users.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <div className="admin-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>account</th>
                <th>access</th>
                <th>{title === 'Requests' ? 'asked' : 'joined'}</th>
                <th>last sign-in</th>
                <th className="num">projects</th>
                <th className="num">runs</th>
                <th className="num">reads</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="admin-who">
                      {u.name ? <span>{u.name}</span> : null}
                      <span className="admin-email">{u.email}{u.emailVerified ? '' : ' (unverified)'}</span>
                      {u.access === 'pending' && u.requestNote ? <span className="admin-note">“{u.requestNote}”</span> : null}
                    </div>
                  </td>
                  <td>
                    <span className={`admin-access ${u.access}`}>{label(u)}</span>
                  </td>
                  <td className="admin-date">{day(u.requestedAt ?? u.createdAt)}</td>
                  <td className="admin-date">{u.lastLoginAt ? day(u.lastLoginAt) : '—'}</td>
                  <td className="num">{u.projects}</td>
                  <td className="num">{u.runs}</td>
                  <td className="num">{u.lifts}</td>
                  <td className="admin-actions">
                    {u.admin || u.id === me.id ? null : (
                      <Actions user={u} busy={busy} act={act} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Actions({ user, busy, act }: { user: AdminUser; busy: boolean; act: Act }) {
  const to = (access: Access, label: string) => (
    <button type="button" className="connect" disabled={busy} onClick={() => act(() => setUserAccess(user.id, access))}>
      {label}
    </button>
  );
  switch (user.access) {
    case 'pending':
      return <>{to('active', 'Let in')}{to('blocked', 'Block')}</>;
    case 'active':
      return to('blocked', 'Block');
    case 'blocked':
      return to('active', 'Unblock');
  }
}

function InviteForm({ busy, act }: { busy: boolean; act: Act }) {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');

  return (
    <section className="admin-section">
      <h2>Invite</h2>
      <p className="muted">
        An invited address is let straight in when it signs in with Google. If it is
        already waiting, it is let in now.
      </p>
      <form
        className="admin-invite"
        onSubmit={(event) => {
          event.preventDefault();
          act(async () => {
            const effect = await invite(email, note);
            setEmail('');
            setNote('');
            return effect;
          });
        }}
      >
        <input
          type="email"
          required
          placeholder="tester@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-label="Email to invite"
        />
        <input
          type="text"
          placeholder="note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          aria-label="Note"
        />
        <button type="submit" className="connect" disabled={busy || email.trim() === ''}>
          Invite
        </button>
      </form>
    </section>
  );
}

function Invites({ invites, busy, act }: { invites: Invite[]; busy: boolean; act: Act }) {
  if (invites.length === 0) return null;
  return (
    <section className="admin-section">
      <h2>
        Invitations <span className="admin-count">{invites.length}</span>
      </h2>
      <div className="admin-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>email</th>
              <th>note</th>
              <th>invited</th>
              <th>signed in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {invites.map((i) => (
              <tr key={i.email}>
                <td className="admin-email">{i.email}</td>
                <td className="muted">{i.note ?? ''}</td>
                <td className="admin-date">
                  {day(i.createdAt)}
                  {i.invitedBy ? ` by ${i.invitedBy}` : ''}
                </td>
                <td className="admin-date">{i.acceptedAt ? day(i.acceptedAt) : 'not yet'}</td>
                <td className="admin-actions">
                  <button type="button" className="connect" disabled={busy} onClick={() => act(() => withdrawInvite(i.email))}>
                    Withdraw
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const label = (u: AdminUser): string =>
  u.admin ? 'admin' : u.access === 'pending' && !u.requestedAt ? 'signed in' : u.access;

const day = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
